import { message } from "ant-design-vue";
import hljs from "highlight.js";
import { Marked } from "marked";
import { markedHighlight } from "marked-highlight";
import md5 from "md5";
import { v1 as uuidv1 } from "uuid";

/**
 * 已经动态导入的js列表
 */
const importScriptList: string[] = [];

const wordType = ["doc", "dot", "wps", "wpt", "docx", "dotx", "docm", "dotm"];
const xlsType = ["xls", "xlt", "et", "xlsx", "xltx", "xlsm", "xltm"];
const pptType = ["ppt", "pptx", "pptm", "ppsx", "ppsm", "pps", "potx", "potm", "dpt", "dps"];
const pdfType = ["pdf"];
const imageType = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "ico"];
const videoType = ["mp4", "webm", "ogg", "avi", "mov", "mpg", "wmv", "ogv"];
const audioType = ["mp3", "wav", "acc", "webm", "m4a"];
const txtType = ["txt"];
const zipType = ["7z", "rar", "zip", "jar"];

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class Common {
  /**
   * 生成UUID
   */
  static getUUID() {
    return uuidv1().replace(/-/g, "");
  }

  /**
   * 生成32位唯一ID（用于文本分析sessionId）
   * @returns {string} 32位的随机字符串
   */
  static generate32BitUniqueId(): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * 获取设备唯一ID（准确性不高）
   */
  static deviceUUID() {
    const canvas = document.createElement("canvas");
    canvas.width = 220;
    canvas.height = 30;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const txt = "data.ailawyers.cn <canvas> 1.0";
      ctx.textBaseline = "top";
      ctx.font = "14px 'Arial'";
      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText(txt, 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText(txt, 4, 17);
      const dataURL = canvas.toDataURL();
      if (dataURL) {
        return md5(dataURL).toUpperCase();
      }
    }
    return this.getUUID();
  }

  /**
   * 数字转千分位
   * @param num
   */
  static toThousands(num: number | string) {
    if (`${num}`.trim() === "") {
      return "";
    }
    num = `${num}`;
    if (/^[^\n\r.\u2028\u2029]*\..*$/.test(num)) {
      const pointIndex = num.lastIndexOf(".");
      let intPart = num.substring(0, pointIndex);
      const pointPart = num.substring(pointIndex + 1, num.length);
      intPart = `${intPart}`;
      const re = /(-?\d+)(\d{3})/;
      while (re.test(intPart)) {
        intPart = intPart.replace(re, "$1,$2");
      }
      num = `${intPart}.${pointPart}`;
    } else {
      num = `${num}`;
      const re = /(-?\d+)(\d{3})/;
      while (re.test(num)) {
        num = num.replace(re, "$1,$2");
      }
    }
    return num;
  }

  /**
   * 打开新窗口，防止被浏览器拦截
   * @param url
   */
  static openWin(url: string) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.click();
  }

  /**
   * 自身窗口内跳转
   * @param url
   */
  static openDlsBuyWin(url: string) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_self";
    a.click();
  }

  /**
   * 打开页面，防止被浏览器拦截
   * @param url
   */
  static goto(url: string) {
    const a = document.createElement("a");
    a.href = url;
    a.click();
  }

  /**
   * 金额格式化、千分位
   * @param {number|string} num 金额
   * @param {number} cent 保留几位小数(默认2位)
   */
  static formatAmount(num: any, cent = 2) {
    if (typeof num !== "number") {
      num = parseFloat(num);
    }
    let bl = 1; //倍数
    for (let i = 1; i <= cent; i++) {
      bl *= 10; //每多一位小数倍数就要乘10
    }
    num = Math.floor(num * bl) / bl; //向下取整再除以倍数
    const b = num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
    return b;
  }

  /**
   * 数字格式化千分位
   * @param num
   */
  static formatThousands(num: any) {
    if (typeof num !== "number") {
      num = parseInt(num);
    }
    const bl = 1; //倍数
    num = Math.floor(num * bl) / bl; //向下取整再除以倍数
    const b = num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
    return b.split(".")[0];
  }

  /**
   * 人品币单位转换
   * @param {number} num 金额(元)
   */
  static RMBUnitConvert(num: number) {
    const moneyUnits = ["元", "万元", "亿元", "万亿"];
    const dividend = 10000;
    let curentNum = num; //转换数字
    let curentUnit = moneyUnits[0]; //转换单位
    for (let i = 0; i < 4; i++) {
      curentUnit = moneyUnits[i];
      if (this.strNumSize(curentNum) < 5) {
        break;
      }
      curentNum = curentNum / dividend;
    }
    const m = { num: "0", unit: "" };
    m.num = curentNum.toFixed(2);
    m.unit = curentUnit;
    return m.num + m.unit;
  }

  /**
   * 数字金额大写转换(可以处理整数,小数,负数)
   * @param {number} n
   */
  static smalltoBIG(n: number) {
    const fraction = ["角", "分"];
    const digit = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
    const unit = [
      ["元", "万", "亿"],
      ["", "拾", "佰", "仟"],
    ];
    const head = n < 0 ? "欠" : "";
    n = Math.abs(n);

    let s = "";

    for (let i = 0; i < fraction.length; i++) {
      s += (digit[Math.floor(n * 10 * 10 ** i) % 10] + fraction[i]).replace(/零./, "");
    }
    s = s || "整";
    n = Math.floor(n);

    for (let i = 0; i < unit[0].length && n > 0; i++) {
      let p = "";
      for (let j = 0; j < unit[1].length && n > 0; j++) {
        p = digit[n % 10] + unit[1][j] + p;
        n = Math.floor(n / 10);
      }
      s = p.replace(/(零.)*零$/, "").replace(/^$/, "零") + unit[0][i] + s;
    }
    return (
      head +
      s
        .replace(/(零.)*零元/, "元")
        .replace(/(零.)+/g, "零")
        .replace(/^整$/, "零元整")
    );
  }

  static strNumSize(tempNum: number) {
    const stringNum = tempNum.toString();
    const index = stringNum.indexOf(".");
    let newNum = stringNum;
    if (index !== -1) {
      newNum = stringNum.substring(0, index);
    }
    return newNum.length;
  }

  /**
   * 对象克隆
   * @param obj 需要克隆的对象
   */
  static clone(obj: any) {
    let result: any;
    const oClass = this.isClass(obj);
    if (oClass === "Object") {
      result = {};
    } else if (oClass === "Array") {
      result = [];
    } else {
      return obj;
    }
    for (const key in obj) {
      const copy = obj[key];
      switch (this.isClass(copy)) {
        case "Object":
        case "Array":
          //递归调用
          result[key] = this.clone(copy);
          break;
        default:
          result[key] = copy;
      }
    }
    return result;
  }

  /**
   * 返回传递给他的任意对象的类
   */
  static isClass(o: any) {
    return Object.prototype.toString.call(o).slice(8, -1);
  }

  /**
   * 判断元素是否在可视区域内
   */
  static isElementInViewport(el: any, offset = 0) {
    if (el.$el) el = el.$el;
    const box = el.getBoundingClientRect();
    const top = box.top >= 0;
    const left = box.left >= 0;
    const bottom = box.bottom <= window.innerHeight + offset;
    const right = box.right <= window.innerWidth + offset;
    return top && left && bottom && right;
  }

  /**
   * 对象转URL参数
   * @param param 需要转换的对象
   * @param key Key值（默认为空）
   * @param encode 是否编码（默认为true）
   */
  static urlEncode(param: any, key: string = "", encode: boolean = true, deep = 0) {
    if (param === null) return "";
    let paramStr = "";
    const t = typeof param;
    if (t === "string" || t === "number" || t === "boolean") {
      paramStr += `&${key}=${encode === null || encode ? encodeURIComponent(param) : param}`;
    } else {
      for (const i in param) {
        const k = key === "" ? i : key + (param instanceof Array ? `[${i}]` : `.${i}`);
        paramStr += this.urlEncode(param[i], k, encode, deep + 1);
      }
    }
    if (deep === 0) {
      return `?${paramStr.substring(1)}`;
    }
    return paramStr;
  }

  /**
   * 获取URL上传所有参数
   */
  static getUrlParams(url?: string) {
    if (!url) {
      url = location.href;
    }
    const pattern = /(\w+|[\u4e00-\u9fa5]+)=(\w+|[\u4e00-\u9fa5]+)/g;
    const result: any = {};
    url.replace(pattern, ($, $1, $2) => {
      result[$1] = $2;
      return "";
    });
    return result;
  }

  /**
   * 动态引入js，重复的url不会重复导入
   * @param { string } src
   */
  static importScript(src: string) {
    if (importScriptList.includes(src)) return;
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.src = src;
    document.body.appendChild(s);
    importScriptList.push(src);
  }

  /**
   * 计算文件大小
   * @param size 文件字节数
   */
  static getFileSize(size: number) {
    if (size < 1024) {
      return `${size}B`;
    } else if (size < 1048576) {
      return `${(size / 1024).toFixed(2)}KB`;
    } else if (size < 1073741824) {
      return `${(size / 1048576).toFixed(2)}MB`;
    } else {
      return `${(size / 1073741824).toFixed(2)}G`;
    }
  }

  /**
   * 获取文件类型
   */
  static getFileType(fileName: string) {
    const type = "file";
    if (fileName && fileName.trim() && fileName.includes(".")) {
      fileName = fileName.trim();
      const suffix = fileName.substring(fileName.lastIndexOf(".") + 1);
      if (wordType.includes(suffix)) {
        return "word";
      } else if (xlsType.includes(suffix)) {
        return "excel";
      } else if (pptType.includes(suffix)) {
        return "ppt";
      } else if (pdfType.includes(suffix)) {
        return "pdf";
      } else if (imageType.includes(suffix)) {
        return "image";
      } else if (videoType.includes(suffix)) {
        return "video";
      } else if (audioType.includes(suffix)) {
        return "audio";
      } else if (txtType.includes(suffix)) {
        return "txt";
      } else if (zipType.includes(suffix)) {
        return "zip";
      }
    }
    return type;
  }

  /**
   * 多个换行转换为一个换行
   */
  static cleanMultiLine(str: string) {
    if (str) {
      return str
        .trim()
        .replace(/\r\n/g, "\n")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, "\n");
    }
    return str;
  }

  static getCaseFormat(text: string) {
    const incs = [
      "本院认为",
      "本院认定事实",
      "本院经审理认定事实",
      "本院经审查审查认为",
      "本院认定案件事实",
      "本院确认以下案件事实",
      "本院认定如下案件事实",
      "本院确认本案法律事实",
      "本院对本案事实认定",
      "确认如下案件事实",
      "确认以下案件事实",
      "本院确认如下法律事实",
      "本院确认本案如下法律事实",
      "根据有效证据可以认定以下事实",
      "认定如下事实",
      "认定以下事实",
      "确认以下事实",
      "确认如下事实",
      "查明事实",
      "本院意见",
      "认定事实",
    ];
    for (let i = 0; i < incs.length; i++) {
      const str = incs[i];
      const pattern = `^${str}[,，:：]?`;
      const regExp = new RegExp(pattern);
      if (regExp.test(text)) {
        text = text.replace(regExp, "");
        return {
          label: `${str}：`,
          text,
        };
      }
    }
    return null;
  }

  //移除HTML标签
  static cleanHTMLTag(html: string) {
    if (html) {
      return html.replace(/<[^>]+(>|$)/g, "");
    }
    return html;
  }

  //移除指定HTML标签
  static cleanTargetTag(html: string, selectors: string): string {
    if (html) {
      const div = document.createElement("div");
      div.innerHTML = html;
      const emList = div.querySelectorAll(selectors);
      emList.forEach((el) => {
        el.outerHTML = el.innerHTML;
      });
      html = div.innerHTML;
      //检查是否还存在该标签
      const ems = div.querySelectorAll(selectors);
      if (ems.length > 0) {
        return this.cleanTargetTag(html, selectors);
      }
    }
    return html;
  }

  //移除文中的图片
  static cleanHTMLImg(html: string) {
    if (html) {
      const div = document.createElement("div");
      div.innerHTML = html;
      const elems = div.querySelectorAll("img");
      for (let i = 0; i < elems.length; i++) {
        const elem = elems[i] as any;
        elem.remove();
      }
      html = div.innerHTML;
    }
    return html;
  }

  /**
   * 移除HTML样式
   */
  static cleanHTMLStyle(html: string) {
    if (html) {
      const div = document.createElement("div");
      div.innerHTML = html;
      const elems = div.querySelectorAll("*");
      for (let i = 0; i < elems.length; i++) {
        const elem = elems[i] as any;
        elem.style = {};
        if (!elem.innerText || !elem.innerText.trim()) {
          elem.remove();
        }
      }
      html = div.innerHTML;
    }
    return html;
  }

  /**
   * 获取文件名后缀
   */
  static getFileSuffix(fileName: string) {
    if (fileName && fileName.trim() && fileName.includes(".")) {
      fileName = fileName.trim();
      const suffix = fileName.substring(fileName.lastIndexOf(".") + 1);
      return suffix.toLowerCase();
    }
    return "";
  }

  /**
   * 获取文件名称
   */
  static getFileName(fileName: string) {
    if (fileName && fileName.trim() && fileName.includes(".")) {
      fileName = fileName.trim();
      return fileName.substring(0, fileName.lastIndexOf("."));
    }
    return "";
  }

  /**
   * 根据file获取图片信息
   */
  static getImageInfoByFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (event) => {
        const img = new Image();
        if (event.target?.result) {
          img.src = event.target.result.toString();
        }
        img.onload = () => {
          resolve(img);
        };
        img.onerror = reject;
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 获取分页数据序号
   */
  static getSerial(index: number, pageNo: number, pageSize: number) {
    return (pageNo - 1) * pageSize + (index + 1);
  }

  /**
   * 将base64转换为文件
   * @param dataurl base64字符串
   * @param filename 文件名
   */
  static dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(",") || [];
    const mime = arr[0].match(/:(.*?);/);
    let mimeType = "";
    if (mime !== null) mimeType = mime[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mimeType });
  }

  /**
   * 查找树节点路径
   */
  static findNodesPathObject(tree: any[], target: any, key = "value", path?: any): any[] {
    if (typeof path === "undefined") {
      path = [];
    }
    for (let i = 0; i < tree.length; i++) {
      const tempPath = [...path];
      tempPath.push(tree[i]);
      if (tree[i][key] === target) {
        return tempPath;
      }
      if (tree[i].children) {
        const reuslt = this.findNodesPathObject(tree[i].children, target, key, tempPath);
        if (reuslt && reuslt.length > 0) {
          return reuslt;
        }
      }
    }
    return [];
  }

  /**
   * 树结构转数组
   */
  static treeToArray(nodes: any[], target: any[] = []) {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      target.push(node);
      if (node.children) {
        this.treeToArray(node.children, target);
      }
    }
    return target;
  }

  /**
   * 根据关键字提取摘要
   */
  static textExtract(source: string, keyword: string) {
    const length = 80;
    if (source.length > length) {
      const startIndex = source.indexOf(keyword);
      let extract = source.substring(startIndex, startIndex + length);
      if (extract.length < length) {
        let index = startIndex - (length - extract.length);
        if (index < 0) index = 0;
        extract = source.substring(index, startIndex + length);
        return extract;
      }
      return `${extract}...`;
    }
    return source;
  }

  /**
   * 将数组变为一维数组并且去重
   */
  static flatSet(values: any[]) {
    return Array.from(new Set(values.flat(Infinity)));
  }

  /**
   * 下载文件
   * @param {string} url
   */
  static downloadFile(url: string, nameName: string) {
    //如果是自己的cdn就增加响应头下载
    if (url.includes("delilegal")) {
      url += "?response-content-disposition=attachment";
      if (nameName) {
        url += `; filename=${nameName}`;
      }
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = nameName;
    // a.target = "_blank";
    a.click();
  }

  // 回答中 markdown 转 html 且高亮
  static handleAnswerText(markdown: string) {
    if (!markdown) {
      return "";
    }
    const marked = new Marked(
      {
        pedantic: true,
      },
      markedHighlight({
        langPrefix: "hljs language-",
        highlight(code, lang) {
          const language = hljs.getLanguage(lang) ? lang : "plaintext";
          return hljs.highlight(code, { language }).value;
        },
      }),
    );
    return marked.parse(markdown) as string;
  }

  /**
   * 节流函数
   * @param func 要执行的函数
   * @param delay 节流的时间间隔（毫秒）
   * @returns 节流后的函数
   */
  static throttle<T extends (...args: any[]) => void>(func: T, delay: number) {
    let lastCall = 0;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const context = this;
      const now = Date.now();
      // 如果自上次调用以来已经超过了`delay`时间
      if (now - lastCall >= delay) {
        // 如果有定时器，清除它
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        // 更新上次调用时间
        lastCall = now;
        // 立即执行函数
        func.apply(context, args);
      } else if (!timeout) {
        // 设置超时以确保在最后一个触发时间之后仍然执行
        timeout = setTimeout(
          () => {
            lastCall = Date.now();
            timeout = null;
            func.apply(context, args);
          },
          delay - (now - lastCall),
        );
      }
    };
  }

  static handleCopyText(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          message.success("复制成功");
        })
        .catch((error) => {
          console.error("复制文本到剪贴板时发生错误:", error);
        });
    } else {
      if (!document.getElementById("copyTarget")) {
        const copyTarget = document.createElement("input");
        copyTarget.setAttribute("style", "position:fixed;top:0;left:0;opacity:0;z-index:-1000;");
        copyTarget.setAttribute("id", "copyTarget");
        document.body.appendChild(copyTarget);
      }
      // 复制内容
      const input = document.getElementById("copyTarget") as HTMLInputElement;
      if (input) {
        input.value = text;
        input.select();
        document.execCommand("copy");
        message.success("复制成功");
        input.blur();
      }
    }
  }

  // 获取url参数
  static getQueryString(name: string) {
    if (!name || typeof name !== "string") {
      return null;
    }
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const value = searchParams.get(name);
      return value ? decodeURIComponent(value) : null;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: any) {
      return null;
    }
  }
  static isFullScreen() {
    if (process.client) {
      const ratio = window.screen.height / window.screen.width;
      const limit = window.screen.height === window.screen.availHeight ? 1.8 : 1.65;
      return ratio > limit; // 通常全面屏的宽高比大于1.8
    }
    return false;
  }

  /**
   * 异步延迟函数
   * @param {number} ms 延迟的毫秒数
   * @returns {Promise<void>} Promise对象
   */
  static sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 验证是否为支持的文档文件类型（doc、docx、txt、pdf）
   * @param {File} file 要验证的文件对象
   * @returns {boolean} 是否为支持的文档文件类型
   */
  static isValidDocumentFile(file: File): boolean {
    // 支持的文件扩展名
    const supportedExtensions = ["doc", "docx", "txt", "pdf"];

    // 支持的 MIME 类型
    const supportedMimeTypes = [
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "text/plain", // .txt
      "application/pdf", // .pdf
    ];

    // 获取文件扩展名
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // 验证扩展名或 MIME 类型，满足其一即可
    return supportedExtensions.includes(fileExtension || "") || supportedMimeTypes.includes(file.type);
  }
}
